/**
 * Spot Color Manager for handling spot color libraries and conversions
 * Supports multiple color systems (Pantone, HKS, etc.) with substrate-specific CMYK approximations
 */

import type {
  SpotColor,
  SpotColorLibrary,
  SpotColorSystem,
  SpotColorConversionOptions,
  SpotColorConversionResult,
  SubstrateProfile,
} from "../../types/substrate";
import type { CMYK } from "../colorEngine";

export class SpotColorManager {
  private libraries: Map<string, SpotColorLibrary> = new Map();
  private loadedLibraries: Set<string> = new Set();

  constructor() {
    // Initialize with empty state - libraries will be loaded on demand
  }

  /**
   * Load a spot color library from JSON data
   */
  async loadLibrary(libraryData: SpotColorLibrary): Promise<void> {
    try {
      // Validate library structure
      this.validateLibrary(libraryData);

      // Store the library
      this.libraries.set(libraryData.id, libraryData);
      this.loadedLibraries.add(libraryData.id);

      console.log(
        `Loaded spot color library: ${libraryData.name} (${libraryData.colors.length} colors)`,
      );
    } catch (error) {
      console.error("Failed to load spot color library:", error);
      throw error;
    }
  }

  /**
   * Load a spot color library from a URL or file path
   */
  async loadLibraryFromUrl(url: string): Promise<void> {
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch library: ${response.statusText}`);
      }

      const libraryData: SpotColorLibrary = await response.json();
      await this.loadLibrary(libraryData);
    } catch (error) {
      console.error(`Failed to load library from URL ${url}:`, error);
      throw error;
    }
  }

  /**
   * Get all loaded libraries
   */
  getLoadedLibraries(): SpotColorLibrary[] {
    return Array.from(this.libraries.values());
  }

  /**
   * Get a specific library by ID
   */
  getLibrary(libraryId: string): SpotColorLibrary | undefined {
    return this.libraries.get(libraryId);
  }

  /**
   * Get all libraries for a specific color system
   */
  getLibrariesBySystem(system: SpotColorSystem): SpotColorLibrary[] {
    return Array.from(this.libraries.values()).filter(
      (lib) => lib.system === system,
    );
  }

  /**
   * Search for spot colors across all loaded libraries
   */
  searchColors(
    query: string,
    options: {
      system?: SpotColorSystem;
      limit?: number;
      includeAliases?: boolean;
    } = {},
  ): SpotColor[] {
    const { system, limit = 50, includeAliases = true } = options;

    const results: SpotColor[] = [];
    const normalizedQuery = query.toLowerCase().trim();

    for (const library of this.libraries.values()) {
      // Filter by system if specified
      if (system && library.system !== system) {
        continue;
      }

      for (const color of library.colors) {
        // Check name match
        if (color.name.toLowerCase().includes(normalizedQuery)) {
          results.push(color);
          continue;
        }

        // Check code match
        if (color.code.toLowerCase().includes(normalizedQuery)) {
          results.push(color);
          continue;
        }

        // Check aliases if enabled
        if (includeAliases && color.aliases) {
          const aliasMatch = color.aliases.some((alias) =>
            alias.toLowerCase().includes(normalizedQuery),
          );
          if (aliasMatch) {
            results.push(color);
            continue;
          }
        }

        // Check tags
        if (color.metadata?.tags) {
          const tagMatch = color.metadata.tags.some((tag) =>
            tag.toLowerCase().includes(normalizedQuery),
          );
          if (tagMatch) {
            results.push(color);
            continue;
          }
        }

        // Stop if we've reached the limit
        if (results.length >= limit) {
          break;
        }
      }

      if (results.length >= limit) {
        break;
      }
    }

    return results.slice(0, limit);
  }

  /**
   * Get a spot color by its unique ID
   */
  getColorById(colorId: string): SpotColor | undefined {
    for (const library of this.libraries.values()) {
      const color = library.colors.find((c) => c.id === colorId);
      if (color) {
        return color;
      }
    }
    return undefined;
  }

  /**
   * Get a spot color by system and code
   */
  getColorByCode(system: SpotColorSystem, code: string): SpotColor | undefined {
    const libraries = this.getLibrariesBySystem(system);

    for (const library of libraries) {
      const color = library.colors.find(
        (c) =>
          c.code.toLowerCase() === code.toLowerCase() ||
          c.aliases?.some(
            (alias) => alias.toLowerCase() === code.toLowerCase(),
          ),
      );
      if (color) {
        return color;
      }
    }

    return undefined;
  }

  /**
   * Convert a spot color to CMYK for a specific substrate
   */
  convertSpotToProcess(
    spotColor: SpotColor,
    substrate: SubstrateProfile,
    options: SpotColorConversionOptions = {
      substrate,
      method: "approximation",
      qualityThreshold: 70,
      allowFallback: true,
    },
  ): SpotColorConversionResult {
    const {
      method,
      qualityThreshold = 70,
      allowFallback = true,
      customConverter,
    } = options;

    // Custom conversion method
    if (method === "custom" && customConverter) {
      try {
        const cmyk = customConverter(spotColor, substrate);
        return {
          spotColor,
          substrate,
          cmyk,
          quality: 100, // Custom conversion gets full quality score
          method: "custom",
          isApproximation: false,
          notes: "Custom conversion function applied",
        };
      } catch (error) {
        console.error("Custom conversion failed:", error);
        if (!allowFallback) {
          throw new Error("Custom conversion failed and fallback not allowed");
        }
      }
    }

    // Spectral conversion (not implemented - would require spectral data)
    if (method === "spectral") {
      if (!allowFallback) {
        throw new Error(
          "Spectral conversion not implemented and fallback not allowed",
        );
      }
      // Fall back to approximation
    }

    // Approximation method
    return this.convertByApproximation(
      spotColor,
      substrate,
      qualityThreshold,
      allowFallback,
    );
  }

  /**
   * Convert spot color using pre-calculated CMYK approximations
   */
  private convertByApproximation(
    spotColor: SpotColor,
    substrate: SubstrateProfile,
    qualityThreshold: number,
    allowFallback: boolean,
  ): SpotColorConversionResult {
    // Try to find exact substrate match
    let approximation = spotColor.cmykApproximations.find(
      (app) => app.substrate === substrate.id,
    );

    // Try to find substrate category match
    if (!approximation) {
      approximation = spotColor.cmykApproximations.find(
        (app) => app.substrate === substrate.category,
      );
    }

    // Try generic matches (with quality penalty)
    if (!approximation) {
      const genericMatches = ["coated", "uncoated", "newsprint"];
      for (const generic of genericMatches) {
        const genericApprox = spotColor.cmykApproximations.find(
          (app) => app.substrate === generic,
        );
        if (genericApprox) {
          // Apply quality penalty for generic matches
          approximation = {
            ...genericApprox,
            quality: Math.max(genericApprox.quality - 20, 50), // Reduce quality by 20%, minimum 50%
          };
          break;
        }
      }
    }

    if (approximation) {
      // Check if quality meets threshold
      if (approximation.quality >= qualityThreshold) {
        return {
          spotColor,
          substrate,
          cmyk: approximation.cmyk,
          quality: approximation.quality,
          method: "approximation",
          isApproximation: true,
          notes:
            approximation.notes ||
            `Approximation for ${approximation.substrate}`,
        };
      } else if (allowFallback) {
        return {
          spotColor,
          substrate,
          cmyk: approximation.cmyk,
          quality: approximation.quality,
          method: "approximation",
          isApproximation: true,
          notes: `Low quality approximation (${approximation.quality}% < ${qualityThreshold}% threshold) - ${approximation.notes}`,
        };
      } else {
        throw new Error(
          `No approximation meets quality threshold ${qualityThreshold}%`,
        );
      }
    }

    if (allowFallback) {
      // Create a basic approximation from the spot color's appearance
      const basicCmyk = this.createBasicApproximation(spotColor);
      return {
        spotColor,
        substrate,
        cmyk: basicCmyk,
        quality: 50, // Low quality for basic approximation
        method: "approximation",
        isApproximation: true,
        notes: "Basic approximation from RGB values - quality may be poor",
      };
    }

    throw new Error("No CMYK approximation available for this substrate");
  }

  /**
   * Create a basic CMYK approximation from RGB values
   */
  private createBasicApproximation(spotColor: SpotColor): CMYK {
    const { r, g, b } = spotColor.appearance.rgb;

    // Simple RGB to CMYK conversion
    const c = 1 - r / 255;
    const m = 1 - g / 255;
    const y = 1 - b / 255;
    const k = Math.min(c, m, y);

    return {
      c: Math.round((c - k) * 100),
      m: Math.round((m - k) * 100),
      y: Math.round((y - k) * 100),
      k: Math.round(k * 100),
    };
  }

  /**
   * Get alternative conversions for a spot color
   */
  getAlternativeConversions(
    spotColor: SpotColor,
    substrate: SubstrateProfile,
    maxAlternatives: number = 3,
  ): SpotColorConversionResult[] {
    const results: SpotColorConversionResult[] = [];

    // Get all approximations for this substrate or category
    const relevantApproximations = spotColor.cmykApproximations.filter(
      (app) =>
        app.substrate === substrate.id ||
        app.substrate === substrate.category ||
        ["coated", "uncoated", "newsprint"].includes(app.substrate),
    );

    // Sort by quality (highest first)
    relevantApproximations.sort((a, b) => b.quality - a.quality);

    // Create conversion results for each approximation
    for (const approximation of relevantApproximations.slice(
      0,
      maxAlternatives,
    )) {
      results.push({
        spotColor,
        substrate,
        cmyk: approximation.cmyk,
        quality: approximation.quality,
        method: "approximation",
        isApproximation: true,
        notes:
          approximation.notes || `Approximation for ${approximation.substrate}`,
      });
    }

    return results;
  }

  /**
   * Validate spot color library structure
   */
  private validateLibrary(library: SpotColorLibrary): void {
    if (!library.id || !library.name || !library.system || !library.version) {
      throw new Error(
        "Library missing required fields: id, name, system, version",
      );
    }

    if (!Array.isArray(library.colors) || library.colors.length === 0) {
      throw new Error("Library must contain at least one color");
    }

    // Validate each color
    library.colors.forEach((color, index) => {
      if (!color.id || !color.name || !color.system || !color.code) {
        throw new Error(
          `Color ${index} missing required fields: id, name, system, code`,
        );
      }

      if (!color.appearance || !color.appearance.hex || !color.appearance.rgb) {
        throw new Error(`Color ${index} missing appearance data`);
      }

      if (
        !Array.isArray(color.cmykApproximations) ||
        color.cmykApproximations.length === 0
      ) {
        throw new Error(
          `Color ${index} must have at least one CMYK approximation`,
        );
      }
    });
  }

  /**
   * Export a library as JSON
   */
  exportLibrary(libraryId: string): string | null {
    const library = this.libraries.get(libraryId);
    if (!library) {
      return null;
    }

    return JSON.stringify(library, null, 2);
  }

  /**
   * Get statistics about loaded libraries
   */
  getStatistics(): {
    totalLibraries: number;
    totalColors: number;
    systems: { [key: string]: number };
    libraries: Array<{
      id: string;
      name: string;
      system: string;
      colorCount: number;
    }>;
  } {
    const systems: { [key: string]: number } = {};
    const libraries: Array<{
      id: string;
      name: string;
      system: string;
      colorCount: number;
    }> = [];

    let totalColors = 0;

    for (const library of this.libraries.values()) {
      totalColors += library.colors.length;
      systems[library.system] = (systems[library.system] || 0) + 1;

      libraries.push({
        id: library.id,
        name: library.name,
        system: library.system,
        colorCount: library.colors.length,
      });
    }

    return {
      totalLibraries: this.libraries.size,
      totalColors,
      systems,
      libraries,
    };
  }

  /**
   * Clear all loaded libraries
   */
  clearLibraries(): void {
    this.libraries.clear();
    this.loadedLibraries.clear();
  }

  /**
   * Get recommended spot colors for a substrate
   */
  getRecommendedColors(
    substrate: SubstrateProfile,
    limit: number = 10,
  ): SpotColor[] {
    const recommendations: Array<{ color: SpotColor; quality: number }> = [];

    for (const library of this.libraries.values()) {
      for (const color of library.colors) {
        // Find best approximation for this substrate
        const bestApprox = color.cmykApproximations.reduce((best, current) => {
          const isBetterMatch =
            (current.substrate === substrate.id &&
              best.substrate !== substrate.id) ||
            (current.substrate === substrate.category &&
              best.substrate !== substrate.id &&
              best.substrate !== substrate.category) ||
            (current.quality > best.quality &&
              current.substrate === best.substrate);

          return isBetterMatch ? current : best;
        });

        // Only include colors with good approximations
        if (bestApprox.quality >= 75) {
          recommendations.push({ color, quality: bestApprox.quality });
        }
      }
    }

    // Sort by quality and return top recommendations
    recommendations.sort((a, b) => b.quality - a.quality);
    return recommendations.slice(0, limit).map((rec) => rec.color);
  }
}
